import { useCallback, useEffect, useState } from "react";
import { Barcode, ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const PAGE_SIZE = 50;

/** FileMaker DateCreated is MM/DD/YYYY — render as YYYY-MM-DD. */
const fmtDate = (v) => {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : (s || "—");
};

/** 2026-09 · UPC 查询页 — browse / search every UPC synced from FileMaker (UPC_API). */
export default function UpcCodes() {
  const { t } = useLanguage();
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedDates, setAppliedDates] = useState({ from: "", to: "" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/upc/search", {
        params: {
          q: query, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
          ...(appliedDates.from ? { date_from: appliedDates.from } : {}),
          ...(appliedDates.to ? { date_to: appliedDates.to } : {}),
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [query, page, appliedDates]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const runSearch = () => {
    setPage(1);
    setQuery(term.trim());
    setAppliedDates({ from: dateFrom, to: dateTo });
  };
  const clearFilters = () => {
    setTerm(""); setDateFrom(""); setDateTo("");
    setPage(1); setQuery(""); setAppliedDates({ from: "", to: "" });
  };

  return (
    <div data-testid="upc-codes-page">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h1 className="text-base font-bold tracking-tight flex items-center gap-2" data-testid="upc-codes-title">
          <Barcode size={16} className="text-brand" /> {t("upc.pageTitle")}
        </h1>
        <span className="font-mono text-xs text-muted-foreground" data-testid="upc-codes-total">{total}</span>
        <div className="flex gap-2 ml-auto">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
              placeholder={t("upc.searchPlaceholder")}
              className="h-9 w-[260px] pl-7 text-sm" data-testid="upc-codes-search-input" />
          </div>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 w-[138px] text-xs" data-testid="upc-codes-date-from" />
          <span className="self-center text-xs text-muted-foreground">~</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="h-9 w-[138px] text-xs" data-testid="upc-codes-date-to" />
          <Button size="sm" className="h-9 rounded-md bg-brand hover:bg-brand-hover" onClick={runSearch}
            data-testid="upc-codes-search-button">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          </Button>
          {(query || appliedDates.from || appliedDates.to) && (
            <Button variant="outline" size="sm" className="h-9 rounded-md text-xs" onClick={clearFilters}
              data-testid="upc-codes-clear-button">
              {t("upc.clearFilters")}
            </Button>
          )}
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-md" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="upc-codes-prev">
              <ChevronLeft size={15} />
            </Button>
            <span className="text-xs font-mono px-1 tabular-nums" data-testid="upc-codes-page">{page} / {totalPages}</span>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-md" disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="upc-codes-next">
              <ChevronRight size={15} />
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-xs" data-testid="upc-codes-table">
          <thead className="bg-secondary/60">
            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 text-left">{t("upc.itemNo")}</th>
              <th className="px-3 py-2 text-left">{t("upc.description")}</th>
              <th className="px-3 py-2 text-left">{t("upc.code")}</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">{t("upc.seq5")}</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">{t("upc.dateCreated")}</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted-foreground py-10" data-testid="upc-codes-empty">
                {t("upc.empty")}
              </td></tr>
            )}
            {items.map((u) => (
              <tr key={`${u.fm_record_id}`} className="border-t border-border/60 hover:bg-secondary/40 transition-colors"
                data-testid={`upc-codes-row-${u.upc_final}`}>
                <td className="px-3 py-1.5 font-mono font-semibold">{u.item_no}</td>
                <td className="px-3 py-1.5 max-w-[420px] truncate" title={u.description}>{u.description}</td>
                <td className="px-3 py-1.5 font-mono">{u.upc_final}</td>
                <td className="px-3 py-1.5 font-mono text-muted-foreground">{u.upc5}</td>
                <td className="px-3 py-1.5 font-mono text-muted-foreground whitespace-nowrap">{fmtDate(u.date_created)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
